import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';

const SOCKET_URL =
  process.env.REACT_APP_WS_URL || (typeof window !== 'undefined' ? window.location.origin : '');

const getAuthToken = () => {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem('authToken') || '';
};

export const usePanicSocket = (onIncident) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastIncident, setLastIncident] = useState(null);
  const socketRef = useRef(null);
  const handlerRef = useRef(onIncident);

  useEffect(() => {
    handlerRef.current = onIncident;
  }, [onIncident]);

  const connectSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    socketRef.current = io(SOCKET_URL, {
      path: '/socket.io',
      transports: ['polling', 'websocket'],
      withCredentials: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
      autoConnect: true,
      auth: { token: getAuthToken() },
    });

    const socket = socketRef.current;

    socket.on('connect', () => setIsConnected(true));
    socket.on('disconnect', () => setIsConnected(false));

    socket.on('panic:incident', (payload) => {
      setLastIncident(payload);
      if (handlerRef.current) handlerRef.current(payload);
    });
  }, []);

  useEffect(() => {
    if (!onIncident) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setIsConnected(false);
      return undefined;
    }

    connectSocket();

    const handleAuthUpdate = () => connectSocket();
    window.addEventListener('authTokenUpdated', handleAuthUpdate);

    return () => {
      window.removeEventListener('authTokenUpdated', handleAuthUpdate);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [onIncident, connectSocket]);

  const clearLastIncident = useCallback(() => {
    setLastIncident(null);
  }, []);

  return { isConnected, lastIncident, clearLastIncident };
};
